// @react
import React, { ReactElement, useContext, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
// @types
import { GetUserQueryResult } from 'lib/graphql/graphqlTypes'
import { CommunicationFormValues } from 'modules/common/forms/types/communication'
// @graphQL
// @libraries
import { DevTool } from '@hookform/devtools'
import { makeStyles } from '@material-ui/core/styles'
import { Slide } from '@material-ui/core'
// @commons
import strings from 'lib/constants/strings'
// @components
import Container from '@bit/atd.web.container'
import Grid from '@bit/atd.web.grid'
import Spacer from '@bit/atd.web.spacer'
import Box from '@bit/atd.web.box'
// @module components
import Navigation from 'modules/onBoarding/components/_common/Navigation'
import EmailUpdateOptionsFields from 'modules/common/forms/fields/communication/EmailUpdateOptions'
import FewerEmailOptionsFields from 'modules/common/forms/fields/communication/FewerEmailOptions'
import PostalOptionFields from 'modules/common/forms/fields/communication/PostalOptions'
import useFormWrapper from 'modules/common/forms/hooks/useCommunicationFormWrapper'
import PromotionalOptionsFields from 'modules/common/forms/fields/communication/PromitionalOptions'
import SkeletonLoader from '@bit/atd.web.loaders.skeleton'
import useNavigation from 'modules/onBoarding/hooks/useNavigation'
import { Direction, NavigationContext } from 'modules/common/context/navigation'

const useStyles = makeStyles(() => ({
  container: {
    maxWidth: '768px',
    margin: '0 auto',
    '& form': {
      width: '100%',
    },
  },
  formContainer: {
    position: 'relative',
  },
  boxRemoveBottomStyling: {
    boxShadow: 'none',
    borderBottom: 0,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  boxRemoveTopBorderRadius: {
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
  },
  boxBody: {
    padding: 0,
  },
}))

type SelfProps = {
  data: GetUserQueryResult['data']
  isLoading: boolean
}
type PropsType = SelfProps

const CommunicationForm = ({
  data: userData,
  isLoading,
}: PropsType): ReactElement => {
  /****************************************
   * PROPS
   *****************************************/

  /******************************************
   * STATE
   ******************************************/

  const [isMutationSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [slidingIn, setSlidingIn] = useState<boolean>(false)
  const [destination, setDestination] = useState<string>('')
  const [direction, setDirection] = useState<Direction>(Direction.LEFT)
  /****************************
   *  HOOKS
   *****************************/
  const classes = useStyles()
  const history = useHistory()
  const navigation = useContext(NavigationContext)

  useEffect(() => {
    setSlidingIn(true)
  }, [])

  const {
    formMethods,
    control,
    defaultValues,
    userCommunicationPreferencesUpdateHandler,
  } = useFormWrapper({
    data: userData,
  })
  const isFormDirty = Object.keys(formMethods.formState.dirtyFields).length > 0

  const { handleNextClick, handleSaveExit, handlePrevClick } = useNavigation({
    setDestination,
    setDirection,
    setSlidingIn,
    setIsSubmitting,
    isFormDirty,
    updateHandler: userCommunicationPreferencesUpdateHandler,
  })

  /****************************************
   * RENDER
   *****************************************/

  return (
    <>
      {process.env.DEBUG !== 'true' && <DevTool control={control} />}
      <Spacer size={32} fullWidth />

      {/* Form */}
      <form className={classes.formContainer}>
        <Slide
          in={slidingIn}
          timeout={500}
          direction={navigation.direction}
          unmountOnExit
          onExit={() => {
            formMethods.clearErrors()
          }}
          onExited={() => {
            navigation.setDirection(direction)
            history.push(destination)
          }}
        >
          <div>
            <Container>
              <Grid className={classes.container} container>
                <Grid item xs={12}>
                  <Box
                    classNames={{
                      root: classes.boxRemoveBottomStyling,
                      body: classes.boxBody,
                    }}
                    header={{
                      title: `${strings.ONBOARDING.COMMUNICATION.OPTION_CHOOSE_EMAIL_UPDATES}:`,
                    }}
                  >
                    {!isLoading ? (
                      <EmailUpdateOptionsFields
                        isEditing
                        formMethods={formMethods}
                        control={control}
                        data={userData}
                        watchFields={
                          (defaultValues && Object.keys(defaultValues)) || []
                        }
                      />
                    ) : (
                      <SkeletonLoader />
                    )}
                  </Box>

                  <Box
                    classNames={{
                      root: classes.boxRemoveTopBorderRadius,
                      body: classes.boxBody,
                    }}
                  >
                    {!isLoading ? (
                      <PromotionalOptionsFields
                        formMethods={formMethods}
                        control={control}
                        isEditing
                        watchFields={
                          (defaultValues && Object.keys(defaultValues)) || []
                        }
                      />
                    ) : (
                      <SkeletonLoader />
                    )}
                  </Box>

                  <Box
                    classNames={{
                      root: classes.boxRemoveTopBorderRadius,
                      body: classes.boxBody,
                    }}
                    header={{
                      title: `${strings.ONBOARDING.COMMUNICATION.OPTION_CHOOSE_FEWER_EMAILS}:`,
                    }}
                  >
                    {!isLoading ? (
                      <FewerEmailOptionsFields
                        isEditing
                        formMethods={formMethods}
                        control={control}
                        watchFields={
                          (defaultValues && Object.keys(defaultValues)) || []
                        }
                      />
                    ) : (
                      <SkeletonLoader />
                    )}
                  </Box>

                  {
                    <>
                      <Spacer size={24} />
                      <Box
                        classNames={{
                          body: classes.boxBody,
                        }}
                        header={{
                          title: `${strings.ONBOARDING.COMMUNICATION.SELECT_POSTAL_MAIL}:`,
                        }}
                      >
                        {!isLoading ? (
                          <PostalOptionFields
                            isEditing
                            formMethods={formMethods}
                            control={control}
                          />
                        ) : (
                          <SkeletonLoader />
                        )}
                      </Box>
                    </>
                  }
                </Grid>
              </Grid>
            </Container>
          </div>
        </Slide>

        <Navigation
          handleExitOnClick={formMethods.handleSubmit(
            (data: CommunicationFormValues) => handleSaveExit(data),
            (errors) => handleSaveExit(undefined, errors),
          )}
          handlePrevOnClick={formMethods.handleSubmit(
            (data: CommunicationFormValues) => handlePrevClick(data),
            (errors) => handlePrevClick(undefined, errors),
          )}
          handleNextOnClick={formMethods.handleSubmit(
            (data: CommunicationFormValues) => handleNextClick(data),
            (errors) => handleNextClick(undefined, errors),
          )}
          prevPage="Contact"
          nextPage="Communication Preferences"
          isSubmitting={isMutationSubmitting}
          nextDisable={isLoading || isMutationSubmitting}
          exitDisable={isLoading || isMutationSubmitting}
          prevDisable={isLoading || isMutationSubmitting}
        />
      </form>
    </>
  )
}

export default CommunicationForm
